// Calls (office-booking prototype, technical design §6.3), Riverstone's: phones ring here. The phone
// system's webhook records the call and matches the caller to customers before anyone answers, so the
// screen pops with who it is: a known customer with their consent and recent jobs, a choice when several
// customers share the number, or a new-customer form for an unknown one. From there, book.
// What the screen holds and reads is calls/use-calls-screen.ts's; Blueprint draws it as calls/CallsPage.tsx
// (an open call, calls/OpenCall.tsx).

import type { ReactNode } from 'react';
import { client } from './api.ts';
import { Header, type NavProps } from './Nav.tsx';
import { LoadLine } from './shared/Ladder.tsx';
import { NewCustomerForm } from './shared/NewCustomer.tsx';
import './calls.css';
import { phoneText, textablePhone, type Call, type Customer, type JobRow } from './types.ts';
import { openJob } from './job/job-page.ts';
import { CallPayment } from './calls/CallPayment.tsx';
import { CallReasonPart } from './calls/CallReason.tsx';
import { BookingRate, MyRate } from './calls/BookingRate.tsx';
import { MIRROR_LINE } from './shared/mirror.ts';
import { CallGlance } from './calls/CallGlance.tsx';
import { campaignWord, lineWord } from './calls/source-model.ts';
import { CallTranscript, RecordingsToMatch } from './calls/CallTranscript.tsx';
import { Callbacks } from './calls/Callbacks.tsx';
import { countLabel } from './calls/callbacks-model.ts';
import { openJobsFirst, type Glance } from './calls/glance-model.ts';
import { BookForm } from './Book.tsx';
import { plainText } from './shared/plain-text.ts';
import { jobStatusWord } from '@flowology/ui-web/words';
import { AdClickOf } from './shared/AdClick.tsx';
import { stillRinging } from './calls/ringing-model.ts';
import { CALL_NOTE, STATUS, useCallsScreen } from './calls/use-calls-screen.ts';

const OUTCOME: Record<string, string> = {
  booked: 'Booked',
  not_booked: 'Not booked',
};
/** A recent call's chip: booked, its reason, or how it went. */
const outcomeWord = (c: Call) =>
  c.outcome === 'booked' ? 'Booked' : c.reason ? c.reason.name : c.outcome ? OUTCOME[c.outcome] : null;

function ConsentChip({ c }: { c: Customer }) {
  const t = textablePhone(c);
  if (c.doNotContact) return <span className="chip say bad">Do not contact</span>;
  return 'method' in t ? (
    <span className="chip say ok">OK to text</span>
  ) : (
    <span className="chip say warn">No texts</span>
  );
}

function CustomerCard({
  c,
  jobs,
  glance,
  payment,
  timeZone,
}: {
  c: Customer;
  jobs: JobRow[];
  glance: { g: Glance | null; owingCents?: number } | null;
  payment?: ReactNode;
  timeZone: string;
}) {
  const loc = c.locations[0];
  const contact = c.contacts[0];
  const phone = contact?.methods.find((m) => m.kind === 'phone');
  return (
    <section className="stone card">
      <h2 className="h3">{c.displayName}</h2>
      <div className="ink2">
        {contact?.name}
        {phone && ` · ${phoneText(phone.value)}`}
      </div>
      {loc && (
        <div className="ink2">
          {loc.line1}, {loc.city}
          {c.locations.length > 1 &&
            ` · +${c.locations.length - 1} more location${c.locations.length > 2 ? 's' : ''}`}
        </div>
      )}
      <div className="tagrow">
        <ConsentChip c={c} />
        <span className="chip say">Customer {c.number}</span>
        {c.kind === 'commercial' && <span className="chip say">Commercial</span>}
      </div>
      {loc?.accessNotes && <p className="ink2">Access: {loc.accessNotes}</p>}
      {glance && <CallGlance g={glance.g} owingCents={glance.owingCents} timeZone={timeZone} />}
      <h3 className="h4">Recent jobs</h3>
      {jobs.length ? (
        <ul className="hist">
          {openJobsFirst(jobs).map((j) => (
            <li key={j.id}>
              {/* the job sheet: its estimates, and Cancel */}
              <button type="button" className="row-btn" onClick={() => openJob(j.id)}>
                <span className="who-col">
                  <b>#{j.number}</b> {plainText(j.summary)}
                </span>
                <span className="chip say">{jobStatusWord(j.status)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No jobs yet.</p>
      )}
      {payment}
    </section>
  );
}

function NewCustomer({ call, onCreated }: { call: Call; onCreated: (customerId: string) => void }) {
  return (
    <section className="stone card">
      <h2 className="h3">No customer has {phoneText(call.from)}</h2>
      <h3 className="h4">New customer</h3>
      <NewCustomerForm
        phone={call.from}
        who="this caller"
        consentNote="Consent (asked on this call; saved with who and when)"
        submitLabel={<>Create customer &amp; book</>}
        link={(customerId, idempotencyKey) =>
          client.call(
            'calls.update',
            { id: call.id, customerId },
            idempotencyKey ? { idempotencyKey } : undefined,
          )
        }
        onDone={onCreated}
      />
    </section>
  );
}

export function CallsScreen({
  nav,
  callId,
  onOpenCall,
  onBoard,
}: {
  nav: NavProps;
  callId: string | null;
  onOpenCall: (id: string | null) => void;
  /** The booking form's way to the board (it offers it only while the company mirrors). */
  onBoard: () => void;
}) {
  const s = useCallsScreen({ nav, callId });
  const { call, customer, mirroring, held, everyone, records, clockOf, whenOf, zone, notes } = s;
  const bar = (w = '') => <span className={`ph-bar ${w}`} aria-hidden="true" />;

  /** One call as a queue row: when, who, its state; the open one marked. */
  const row = (c: Call) => (
    <li key={c.id}>
      <button className={`row-btn${c.id === callId ? ' on' : ''}`} onClick={() => onOpenCall(c.id)}>
        <span className="t">{clockOf(c.startedAt)}</span>
        <span className="who-col">
          <b>{c.matches[0]?.customerName ?? 'Unknown caller'}</b>
          <span className="ink2">
            {' '}
            · {phoneText(c.from)}
            {c.answeredBy ? ` · Answered by ${c.answeredBy}` : ''}
          </span>
        </span>
        <span className="chip say">{outcomeWord(c) ?? STATUS[c.status]?.label ?? c.status}</span>
      </button>
    </li>
  );
  // ringing now, on top of the queue (BL-099): a call still ringing with nothing said yet, from the last
  // quarter hour (one whose end never came is the recent list's, not a ring)
  const ringing = (s.recent ?? []).filter((c) => stillRinging(c));
  const recentCard = (
    <section className="stone card">
      <h2 className="h4">Recent calls</h2>
      {s.recent === null ? (
        // L-table: blank rows at a row's height, never "No calls yet" before the list is here
        s.recentPhase !== 'error' && (
          <ul className={`calls${s.recentPhase === 'wait' ? ' ldwait' : ''}`} aria-busy="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <li key={i} className="row-ph" aria-hidden="true">
                {bar('t')}
                {bar('w40')}
              </li>
            ))}
          </ul>
        )
      ) : s.recent.length ? (
        <ul className="calls">{s.recent.map(row)}</ul>
      ) : (
        <p className="muted">
          No calls yet. When the phone rings, the caller shows up here before you answer.
        </p>
      )}
      {s.recent === null && <LoadLine phase={s.recentPhase} what="recent calls" onRetry={s.retryRecent} />}
    </section>
  );

  const ringingCard = ringing.length > 0 && (
    <section className="stone card" aria-label="Ringing now">
      <h2 className="h4">Ringing now</h2>
      <ul className="calls">{ringing.map(row)}</ul>
    </section>
  );
  const callbacks = (
    <Callbacks
      calls={s.backs.calls}
      days={s.backs.days}
      failed={s.backs.failed}
      whenOf={whenOf}
      onOpen={(id) => onOpenCall(id)}
      onOlder={s.olderBacks}
      onChanged={s.retryBacks}
    />
  );
  // the open call: its header, what it booked, the booking form, its cards and its pinned actions
  const detail = (
    <>
      {s.callPending && (
        // L-sheet: the pop's place and the call's frame at once; the words arrive together
        <div className={s.callPhase === 'wait' ? 'ldwait' : ''} aria-busy={s.callPhase !== 'error'}>
          <div className="stone now-ph" aria-hidden="true">
            {bar('w40')}
            {bar('w60')}
          </div>
          <div className="grid2">
            <section className="stone card cust-ph">
              {s.callPhase !== 'error' && (
                <>
                  {bar('w60 head')}
                  {bar('w40')}
                  {bar('w40')}
                </>
              )}
            </section>
            <section className="stone card">
              <h2 className="h4">This call</h2>
              <dl className="kv">
                {['From', 'Line', 'Started', 'Outcome'].map((k) => (
                  <div key={k} className="kv-ph">
                    <dt>{k}</dt>
                    <dd>{s.callPhase !== 'error' && bar('w40')}</dd>
                  </div>
                ))}
              </dl>
              <label className="f">
                Call notes (saved on the call)
                <span className="field area ph-field" aria-hidden="true" />
              </label>
              <div className="row">
                <button className="btn primary" disabled>
                  Book a job
                </button>
                <button className="btn" disabled>
                  Not booked
                </button>
              </div>
              {/* the call's one-line note is the slot: "Getting the call…" takes its place at 3 s */}
              <LoadLine phase={s.callPhase} what="the call" note={CALL_NOTE} onRetry={s.retryCall} />
            </section>
          </div>
        </div>
      )}
      {!s.callPending && call && s.status && (
        // the call's header (BL-099): the ringing pop folded into one line above its cards
        <div
          className={`stone now call-head${call.status === 'ringing' ? ' ring' : ''}`}
          role={call.status === 'ringing' ? 'alert' : 'status'}
        >
          <span className={`chip ${s.status.chip}`}>{s.status.label}</span> <b>{s.who}</b>{' '}
          <span className="ink2">
            {phoneText(call.from)} → {phoneText(call.to)} · {clockOf(call.startedAt)} ·{' '}
            {customer
              ? 'Matched to a customer'
              : call.matches.length > 1
                ? 'Pick the customer'
                : 'No customer has this number'}
          </span>
        </div>
      )}

      {!s.callPending && call && s.booked && (
        <p className="notice ok" role="status">
          <b>Booked: job #{s.booked.jobNumber}.</b> It’s on the dispatch board
          {s.booked.texted ? ', and the confirmation text is on its way' : ''}.{' '}
          <button type="button" className="btn quiet sm" onClick={() => openJob(s.booked!.jobId)}>
            Open the job
          </button>
        </p>
      )}

      {!s.callPending && call && s.booking && (
        // the Book screen's form, beside the queue (BL-101): its rules are the Book screen's
        <section className="call-book" ref={s.bookingRef} aria-label="Book a job for this call">
          <div className="row between">
            <h2 className="h3">Book a job</h2>
            <button type="button" className="btn quiet" onClick={s.stopBooking}>
              Back to the call
            </button>
          </div>
          <BookForm nav={nav} start={s.booking} timeZone={zone} onBoard={onBoard} onBooked={s.onBooked} />
        </section>
      )}

      {!s.callPending && call && !s.booking && (
        <div className="grid2">
          {customer ? (
            <CustomerCard
              c={customer}
              jobs={s.jobs}
              timeZone={zone}
              glance={
                s.glance && {
                  g: s.glance.g,
                  owingCents: s.owing?.key === s.glance.key ? s.owing.cents : undefined,
                }
              }
              // payments.record (and its pay link) is the office's (F-40)
              payment={
                !mirroring && held.includes('payments.record') ? (
                  <CallPayment customer={customer} timeZone={zone} tick={s.moneyTick} />
                ) : undefined
              }
            />
          ) : call.matches.length > 1 ? (
            <section className="stone card">
              <h2 className="h3">Who is calling?</h2>
              <p className="ink2">Several customers have {phoneText(call.from)}.</p>
              {mirroring && <p className="muted small">{MIRROR_LINE}</p>}
              {call.matches.map((m) =>
                mirroring ? (
                  <p key={m.customerId + m.contactName} className="small">
                    {m.contactName} · {m.customerName}
                  </p>
                ) : (
                  <button
                    key={m.customerId + m.contactName}
                    className="btn soft wide pick"
                    onClick={() => void s.update({ customerId: m.customerId }).then(s.loadCall)}
                  >
                    {m.contactName} · {m.customerName}
                  </button>
                ),
              )}
            </section>
          ) : mirroring ? (
            <section className="stone card">
              <h2 className="h3">No customer has {phoneText(call.from)}</h2>
              <p className="muted small">{MIRROR_LINE}</p>
            </section>
          ) : (
            <NewCustomer
              call={call}
              onCreated={(id) => {
                s.startBooking({ customerId: id, callId: call.id, notes: notes || null });
                void s.loadCall();
              }}
            />
          )}

          <section className="stone card">
            <h2 className="h4">This call</h2>
            <dl className="kv">
              <dt>From</dt>
              <dd>{phoneText(call.from)}</dd>
              <dt>Line</dt>
              <dd>{lineWord(phoneText(call.to), call.trackingLabel)}</dd>
              {/* where it came from (call-handling item 3, BL-008) */}
              <dt>Source</dt>
              <dd>
                {call.campaign ? campaignWord(call.campaign) : <span className="muted">No campaign</span>}
              </dd>
              <dt>Started</dt>
              <dd>{clockOf(call.startedAt)}</dd>
              {s.callJob && call.jobId && (
                <>
                  <dt>Job</dt>
                  <dd>
                    <button
                      type="button"
                      className="btn quiet sm call-job"
                      onClick={() => openJob(call.jobId!)}
                    >
                      #{s.callJob}
                    </button>
                  </dd>
                </>
              )}
              {call.textedBackAt && (
                <>
                  <dt>Texted back</dt>
                  <dd>{clockOf(call.textedBackAt)}</dd>
                </>
              )}
            </dl>
            {/* the ad click it came from, by the website's tag or the AdTrack import (BL-092) */}
            <AdClickOf of={{ callId: call.id }} timeZone={zone} />
            {/* booked or why not, who took it, a review (BL-003) */}
            <CallReasonPart
              call={call}
              held={held}
              onCall={s.setCall}
              toast={s.setToast}
              pickSignal={s.pickSignal}
              pinned
            />
            {/* the Twilio recording and what was said: owner and managers (BL-083, AD-9) */}
            {everyone && <CallTranscript key={call.id} callId={call.id} known={call.recording} />}
            <label className="f">
              {records ? 'Call notes (saved on the call)' : 'Call notes'}
              <textarea
                className="field area"
                value={notes}
                readOnly={!records}
                placeholder={records ? 'What did they say?' : undefined}
                onChange={(e) => s.typeNotes(call.id, e.target.value)}
                onBlur={() => void s.saveNotes(call.id)}
              />
            </label>
            <p className="muted small">{CALL_NOTE}</p>
          </section>
        </div>
      )}

      {!s.callPending && call && !s.booking && (
        // the call's main actions, pinned at the foot of the screen (BL-099): in view at any height
        <div className="call-actions" role="group" aria-label="This call’s actions">
          {customer && !mirroring && (
            <button
              className="btn primary"
              onClick={() =>
                // the call's address and its notes, as typed (calls item 5)
                s.startBooking({
                  customerId: customer.id,
                  callId: call.id,
                  locationId: call.locationId,
                  notes: notes || null,
                })
              }
            >
              Book a job
            </button>
          )}
          {records && call.outcome !== 'booked' && !(call.reviewed && !everyone) && (
            <button type="button" className="btn" onClick={s.pickReason}>
              {call.reason ? 'Change the reason…' : 'Not booked…'}
            </button>
          )}
          {records && !call.takenBy && call.outcome !== 'booked' && (
            <button
              type="button"
              className="btn soft"
              onClick={() => void s.update({ claim: true }, 'It’s yours to call back.')}
            >
              I’ll call them back
            </button>
          )}
          <button type="button" className="btn quiet" onClick={() => onOpenCall(null)}>
            Close
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="app">
      {/* no lede, and the parts and the booking rate beside the title (BL-099): the call's own header
          says what's ringing and who */}
      <Header nav={nav} title="Calls" toolbar>
        <div className="tabs calls-tabs" role="tablist" aria-label="Calls">
          {(
            [
              ['calls', 'Calls'],
              ['rate', 'Booking rate'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={s.part === k}
              className={s.part === k ? 'on' : ''}
              onClick={() => s.setPart(k)}
            >
              {label}
              {k === 'calls' && !!s.backs.calls?.length && (
                <span className="count" aria-label={countLabel(s.backs.calls.length)}>
                  {s.backs.calls.length}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="calls-myrate">
          <MyRate today={s.today} everyone={everyone} version={s.rateVersion} />
        </div>
      </Header>
      <main className="page calls-page">
        {s.part === 'rate' && <BookingRate today={s.today} everyone={everyone} />}
        {s.part === 'calls' && (
          <div className="calls-layout">
            <aside className="calls-queue" aria-label="Call queue">
              {ringingCard}
              {callbacks}
              {recentCard}
            </aside>
            <div className="calls-main">
              {detail}
              {!callId && (
                <section className="stone card calls-empty">
                  <p className="muted">
                    Pick a call from the queue, or wait for the phone: the caller shows up here before you
                    answer.
                  </p>
                </section>
              )}
              {everyone && <RecordingsToMatch whenOf={whenOf} toast={s.setToast} />}
            </div>
          </div>
        )}
      </main>
      {s.toast && (
        <div className="toast" role="status">
          {s.toast}
        </div>
      )}
    </div>
  );
}
